'use client';
import { Box } from '@wanteddev/wds-engine';
import { IconCircle, IconCircleCheckFill, IconCircleExclamationFill } from '@wanteddev/wds-icon';
import { memo, useId, useState } from 'react';
import { Portal, FlexBox, TextButton } from '../index.mjs';
import { useRegionStore } from '../../stores/region-store.mjs';
import { ellipsisTypographyStyle } from '../../utils/index.mjs';
import Typography from '../typography/index.mjs';
import { isSnackbar } from './helpers.mjs';
import { bottomRegionStatusStyle, firstOverlayStyle, secondOverlayStyle, messageStyle, textStyle, snackbarActionStyle, toastCircleIconWrapperStyle, bottomMountKeyFrames, bottomUnmountKeyFrames } from './style.mjs';
import { jsx, Fragment, jsxs } from 'react/jsx-runtime';

var RegionStatus = () => {
  const items = useRegionStore((state) => state.items);
  return /* @__PURE__ */ jsx(Fragment, { children: items.map((item) => /* @__PURE__ */ jsx(
    Portal,
    {
      container: globalThis?.document?.querySelector(
        "#wds-region-manager-bottom"
      ),
      children: isSnackbar(item) ? /* @__PURE__ */ jsx(Snackbar, { ...item }) : /* @__PURE__ */ jsx(Toast, { ...item })
    },
    item.id
  )) });
};
var Toast = ({
  id,
  duration = 5e3,
  variant = "normal",
  icon = /* @__PURE__ */ jsx(IconCircle, {}),
  ...props
}) => {
  const hide = useRegionStore((state) => state.hide);
  const contentId = useId();
  const [isMountAnimationDone, setIsMountAnimationDone] = useState(false);
  const iconComponent = {
    normal: null,
    success: /* @__PURE__ */ jsx(
      FlexBox,
      {
        alignItems: "center",
        justifyContent: "center",
        sx: toastCircleIconWrapperStyle,
        children: /* @__PURE__ */ jsx(
          IconCircleCheckFill,
          {
            sx: (theme) => ({
              color: theme.palette.status.positive
            })
          }
        )
      }
    ),
    warning: /* @__PURE__ */ jsx(
      FlexBox,
      {
        alignItems: "center",
        justifyContent: "center",
        sx: toastCircleIconWrapperStyle,
        children: /* @__PURE__ */ jsx(
          IconCircleExclamationFill,
          {
            sx: (theme) => ({
              color: theme.palette.status.cautionary
            })
          }
        )
      }
    ),
    custom: icon
  };
  const handleAnimationEnd = (e) => {
    if (e.animationName === bottomMountKeyFrames.name) {
      setIsMountAnimationDone(true);
    } else if (e.animationName === bottomUnmountKeyFrames.name) {
      hide(id);
    }
  };
  return /* @__PURE__ */ jsxs(
    Box,
    {
      "aria-atomic": true,
      role: variant === "warning" ? "alert" : "status",
      "aria-live": variant === "warning" ? "assertive" : "polite",
      sx: bottomRegionStatusStyle(duration, isMountAnimationDone),
      onAnimationEnd: handleAnimationEnd,
      "aria-describedby": contentId,
      children: [
        /* @__PURE__ */ jsx(Box, { role: "presentation", sx: firstOverlayStyle }),
        /* @__PURE__ */ jsx(Box, { role: "presentation", sx: secondOverlayStyle }),
        /* @__PURE__ */ jsxs(
          FlexBox,
          {
            gap: "8px",
            alignItems: "center",
            sx: { ["& svg"]: { flexShrink: 0 } },
            children: [
              iconComponent[variant],
              /* @__PURE__ */ jsx(
                Typography,
                {
                  color: "palette.static.white",
                  variant: "body2_normal",
                  weight: "bold",
                  id: contentId,
                  sx: [messageStyle, textStyle],
                  children: props.content
                }
              )
            ]
          }
        )
      ]
    }
  );
};
var Snackbar = ({
  id,
  duration = 5e3,
  heading,
  description,
  extraContent,
  action
}) => {
  const hide = useRegionStore((state) => state.hide);
  const headingId = useId();
  const descriptionId = useId();
  const [isMountAnimationDone, setIsMountAnimationDone] = useState(false);
  const handleAnimationEnd = (e) => {
    if (e.animationName === bottomMountKeyFrames.name) {
      setIsMountAnimationDone(true);
    } else if (e.animationName === bottomUnmountKeyFrames.name) {
      hide(id);
    }
  };
  return /* @__PURE__ */ jsxs(
    Box,
    {
      "aria-atomic": true,
      role: "status",
      "aria-live": "polite",
      sx: bottomRegionStatusStyle(duration, isMountAnimationDone),
      onAnimationEnd: handleAnimationEnd,
      "aria-labelledby": headingId,
      "aria-describedby": descriptionId,
      children: [
        /* @__PURE__ */ jsx(Box, { role: "presentation", sx: firstOverlayStyle }),
        /* @__PURE__ */ jsx(Box, { role: "presentation", sx: secondOverlayStyle }),
        /* @__PURE__ */ jsxs(FlexBox, { gap: "12px", alignItems: "center", children: [
          extraContent && /* @__PURE__ */ jsx(
            FlexBox,
            {
              flexShrink: 0,
              sx: { width: "fit-content", height: "fit-content" },
              children: extraContent
            }
          ),
          /* @__PURE__ */ jsxs(FlexBox, { flexDirection: "column", sx: messageStyle, children: [
            heading && /* @__PURE__ */ jsx(
              Typography,
              {
                color: "palette.static.white",
                variant: "body2_normal",
                weight: "bold",
                id: headingId,
                sx: textStyle,
                children: heading
              }
            ),
            description && /* @__PURE__ */ jsx(
              Typography,
              {
                color: "palette.static.white",
                variant: "label2",
                weight: "regular",
                id: descriptionId,
                sx: [textStyle, ellipsisTypographyStyle(2)],
                children: description
              }
            )
          ] }),
          /* @__PURE__ */ jsx(
            TextButton,
            {
              variant: "assistive",
              size: "medium",
              ...action,
              sx: [snackbarActionStyle, action.sx]
            }
          )
        ] })
      ]
    }
  );
};
var region_status_default = memo(RegionStatus);

export { region_status_default as default };
